import React, { useState } from 'react';
import { ShieldCheck, Shield, ChevronRight } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { setPatientDetails } from '../../redux/slices/appointmentSlice';
import { InsuranceModal } from './InsuranceModal';

const PAYMENT_OPTIONS: { value: 'insurance' | 'myself'; label: string }[] = [
  { value: 'insurance', label: 'I have insurance' },
  { value: 'myself', label: 'I will pay myself' },
];

export const PaymentInformation: React.FC = () => {
  const dispatch = useDispatch();
  const patientDetails = useSelector((state: RootState) => state.appointment.patientDetails);
  const insuranceSetting = useSelector((state: RootState) => state.auth.practice?.insurance_setting);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handlePaymentTypeChange = (type: 'insurance' | 'myself') => {
    dispatch(setPatientDetails({ ...patientDetails, paymentType: type }));
  };

  // Calculate required fields and completed count
  const getFieldsStatus = () => {
    const insDetails = patientDetails.insuranceDetails;

    // Always required: Provider, Policy Holder Name
    let totalRequired = 2;
    let completed = 0;

    if (insDetails?.provider?.trim()) completed++;
    if (insDetails?.policyHolderName?.trim()) completed++;

    // Conditionally required based on backend settings
    const showDob = insuranceSetting?.display_policy_holder_dob ?? true;
    const requireDob = insuranceSetting?.require_policy_holder_dob ?? false;
    const showMemberId = insuranceSetting?.display_member_id ?? true;
    const requireMemberId = insuranceSetting?.require_member_id ?? false;

    if (showDob && requireDob) {
      totalRequired++;
      if (insDetails?.policyHolderDob) completed++;
    }

    if (showMemberId && requireMemberId) {
      totalRequired++;
      if (insDetails?.memberId?.trim()) completed++;
    }

    return { completed, total: totalRequired };
  };

  const fieldsStatus = getFieldsStatus();
  const isComplete = fieldsStatus.completed === fieldsStatus.total;

  return (
    <div className="">
      <div className="form-control">
        <label className="label">
          <span className="label-text font-semibold text-gray-500">Insurance Information <span className="text-error">*</span></span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
          {PAYMENT_OPTIONS.map(({ value, label }) => {
            const selected = patientDetails.paymentType === value;
            return (
              <label
                key={value}
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 transition-colors ${selected ? 'border-teamcare-blue bg-teamcare-blue/10' : 'border-gray-200 bg-white hover:border-gray-300'}`}
              >
                <input
                  type="radio"
                  name="paymentType"
                  className="radio radio-primary radio-sm"
                  checked={selected}
                  onChange={() => handlePaymentTypeChange(value)}
                />
                <span className={`text-sm font-medium ${selected ? 'text-teamcare-blue-dark' : 'text-gray-600'}`}>
                  {label}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {patientDetails.paymentType === 'insurance' && (
        <button
          onClick={() => setIsModalOpen(true)}
          className={`
            mt-2 w-full md:w-auto
            rounded-lg p-2
            flex items-center gap-4
            shadow-sm
            transition-all duration-500 ease-out
            hover:shadow-lg
            hover:-translate-y-0.5
            hover:scale-[1.02]
            active:scale-[0.98]
            ${isComplete
              ? 'bg-gradient-to-r from-teamcare-cyan to-teamcare-cyan-dark bg-[length:200%_200%] bg-left hover:bg-right'
              : 'bg-white border border-teamcare-cyan'
            }
          `}>
          <div className={`p-2 rounded-lg text-white ${isComplete ? 'bg-white/20 ' : 'bg-teamcare-cyan'}`}>
            {isComplete ? <ShieldCheck size={24} /> : <Shield size={24} />}
          </div>
          <div className="flex-1 text-left">
            <h3 className={`font-semibold text-md leading-tight ${isComplete ? 'text-white' : 'text-gray-600'}`}>
              {isComplete ? 'Insurance Details Added' : 'Add Insurance Details'}
            </h3>
            <p className={`text-xs ${isComplete ? 'text-white/80' : 'text-gray-500'}`}>
              {isComplete ? 'Click to edit your insurance information' : 'Click to enter your insurance information'}
            </p>
          </div>
          <ChevronRight size={24} className={isComplete ? 'text-white/60' : 'text-gray-400'} />
        </button>
      )}

      <InsuranceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isComplete={isComplete}
      />
    </div>
  );
};
